import { childAnalyticEntities, isChildOfEntity } from "building-common/analyticEntity/buildingAnalyticEntitySelectors";
import {
  isBuildingAnalyticExtrusion,
  isBuildingAnalyticLineSegment,
} from "building-common/analyticEntity/buildingAnalyticEntitySelectors";
import { foundationSlabs } from "building-generation/elements/foundation/foundationProfiles";
import type { Building } from "building-types/building/buildingTypes";
import type { BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import { isBuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type {
  GeneratedFoundationSubSystem,
  GeneratedFoundationSuperSystem,
} from "building-types/foundations/foundationTypes";
import type { StandardFoundationKeyMeasureSet } from "building-types/foundations/standardFoundationKeyMeasureTypes";
import { FoundationKeyMeasureType } from "building-types/foundations/standardFoundationKeyMeasureTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { InchesCubed, InchesSquared, WorldInches } from "common/types/nominalNumbersTypes";
import {
  feetCubedFromInchesCubed,
  feetSquaredFromInchesSquared,
  inchesCubedNew,
  inchesSquaredNew,
  worldFeetFromWorldInches,
  worldInchesNew,
} from "common/utils/nominalNumbers";
import { docEntityHandleIsGeneratedEntity } from "doc/entity/docEntityHandleTools";

export function getSlabFoundationsKeyMeasures(
  building: Building,
  foundationSuperSystems: GeneratedFoundationSuperSystem[],
): StandardFoundationKeyMeasureSet {
  const foundationAnalyticEntities = {
    ...building.analyticFoundationEntities,
    ...building.analyticFoundationSuperSystemEntities,
    ...building.analyticFoundationHorizontalAssemblyEntities,
  };
  let slabKeyMeasures = {} as StandardFoundationKeyMeasureSet;

  for (const foundation of foundationSuperSystems) {
    const slabs = foundationSlabs(foundation);
    const kmSet = {} as StandardFoundationKeyMeasureSet;

    for (const slab of slabs) {
      const { roomClass, id } = slab;

      // get all analytic extrusions for which this slab is a parent
      const slabEntities = Object.values(foundationAnalyticEntities)
        .filter(isBuildingAnalyticExtrusion)
        .filter((e) =>
          e.parents
            .filter(docEntityHandleIsGeneratedEntity)
            .map((g) => g.entity)
            .filter(isBuildingEntityHandle)
            .some((h) => h.id === id),
        );

      let area: InchesSquared = inchesSquaredNew(0);
      let volume: InchesCubed = inchesCubedNew(0);
      const handles: DocEntityHandle[] = [];

      // Reporting-unit labels on the foundation super system apply to all slabs under it.
      if (foundation.docEntityHandle) {
        handles.push(foundation.docEntityHandle);
      }

      for (const entity of slabEntities) {
        // Overlap between foundation systems is resolved in generateFoundationSystems before analytic emission.
        area = inchesSquaredNew(area + entity.profileInPlanArea);
        volume = inchesCubedNew(volume + entity.volume);
        handles.push(entity.docEntityHandle);
      }

      // one KAM per slab
      kmSet[id] = {
        category: FoundationKeyMeasureType.SLAB,
        name: RoomClass[roomClass],
        area: feetSquaredFromInchesSquared(area),
        volume: feetCubedFromInchesCubed(volume),
        handles,
        order: 1,
      };
    }
    slabKeyMeasures = { ...slabKeyMeasures, ...kmSet };
  }

  return slabKeyMeasures;
}

export function getFootingFoundationsKeyMeasures(
  building: Pick<
    Building,
    | "analyticFoundationEntities"
    | "analyticFoundationSuperSystemEntities"
    | "analyticFoundationHorizontalAssemblyEntities"
  >,
  foundations: GeneratedFoundationSubSystem[],
): StandardFoundationKeyMeasureSet {
  const foundationAnalyticEntities = {
    ...building.analyticFoundationEntities,
    ...building.analyticFoundationSuperSystemEntities,
    ...building.analyticFoundationHorizontalAssemblyEntities,
  };
  let footingKeyMeasures = {} as StandardFoundationKeyMeasureSet;
  const sectionCategotry = FoundationKeyMeasureType.FOOTING;

  for (const foundation of foundations) {
    const kmSet = {} as StandardFoundationKeyMeasureSet;

    for (const strip of Object.values(foundation.subSystem.strips)) {
      const { id, sections, path } = strip;

      let length: WorldInches = worldInchesNew(0);
      let volume: InchesCubed = inchesCubedNew(0);
      const handles: DocEntityHandle[] = [];

      // Reporting-unit labels on the foundation system apply to all footing strips under it.
      if (foundation.docEntityHandle) {
        handles.push(foundation.docEntityHandle);
      }
      const foundationReportingHandle = foundation.subSystem.entityHandleForSelectingReportingUnit;
      if (foundationReportingHandle) {
        handles.push(foundationReportingHandle);
      }

      // get all analytic line segments for which this strip is a parent
      const edges = childAnalyticEntities(foundationAnalyticEntities, {
        entityType: "FOUNDATION_STRIP",
        id,
      }).filter(isBuildingAnalyticLineSegment);

      for (const edge of edges) {
        length = worldInchesNew(length + edge.length);
        handles.push(edge.docEntityHandle);
      }

      // volume is calculated as the sum of the volumes of analytic extrusions that are
      // children of the individual foundation sections
      for (const section of sections) {
        const { id } = section;

        const sectionHandle: BuildingEntityHandle = { entityType: "FOUNDATION_SECTION", id };

        const sectionExtrusions = Object.values(foundationAnalyticEntities)
          .filter(isBuildingAnalyticExtrusion)
          .filter(isChildOfEntity(sectionHandle));

        for (const entity of sectionExtrusions) {
          volume = inchesCubedNew(volume + entity.volume);
          handles.push(entity.docEntityHandle);
        }
      }

      // one KAM per foundation strip
      kmSet[id] = {
        category: sectionCategotry,
        name: path.condition,
        length: worldFeetFromWorldInches(length),
        order: 0,
        volume: feetCubedFromInchesCubed(volume),
        handles,
      };
    }

    footingKeyMeasures = { ...footingKeyMeasures, ...kmSet };
  }

  return footingKeyMeasures;
}
